import apiClient from './api';

const discountService = {
  // ============ DISCOUNT CRUD ============

  getDiscounts: async (params = {}) => {
    const response = await apiClient.get('/prices/discounts/', { params });
    return response.data;
  },

  getDiscount: async (id) => {
    const response = await apiClient.get(`/prices/discounts/${id}/`);
    return response.data;
  },

  createDiscount: async (data) => {
    const response = await apiClient.post('/prices/discounts/', data);
    return response.data;
  },

  updateDiscount: async (id, data) => {
    const response = await apiClient.patch(`/prices/discounts/${id}/`, data);
    return response.data;
  },

  deleteDiscount: async (id) => {
    await apiClient.delete(`/prices/discounts/${id}/`);
  },

  duplicateDiscount: async (id, newCode) => {
    const response = await apiClient.post(`/prices/discounts/${id}/duplicate/`, {
      code: newCode
    });
    return response.data;
  },

  getCatalogueBadgeInfo: async (id) => {
    const response = await apiClient.get(`/prices/discounts/${id}/catalogue-badge/`);
    return response.data;
  },

  applyCatalogueBadge: async (id, mode, badgeId = null) => {
    const response = await apiClient.post(`/prices/discounts/${id}/catalogue-badge/`, {
      mode,
      badge_id: badgeId,
    });
    return response.data;
  },

  toggleDiscountStatus: async (id) => {
    const response = await apiClient.post(`/prices/discounts/${id}/toggle_status/`);
    return response.data;
  },

  // ============ DISCOUNT CALCULATION ============

  calculateDiscount: async (productId, quantity, customerId = null, unitPrice = null) => {
    const response = await apiClient.post('/prices/discounts/calculate/', {
      product_id: productId,
      quantity: quantity,
      customer_id: customerId,
      unit_price: unitPrice,
    });
    return response.data;
  },

  bulkCalculateDiscounts: async (items, customerId = null) => {
    const response = await apiClient.post('/prices/discounts/bulk_calculate/', {
      items: items,
      customer_id: customerId,
    });
    return response.data;
  },

  getDiscountsForProduct: async (productId, customerId = null) => {
    const params = { product_id: productId };
    if (customerId) {
      params.customer_id = customerId;
    }
    const response = await apiClient.get('/prices/discounts/for_product/', { params });
    return response.data;
  },

  getDiscountsForProducts: async (productIds, customerId = null) => {
    const response = await apiClient.post('/prices/discounts/for_products/', {
      product_ids: productIds,
      customer_id: customerId,
    });
    return response.data;
  },

  processPlaceholders: (text, discount) => {
    if (!text || !discount) return text;

    const discountValue = parseFloat(discount.discount_value) || 0;
    const originalPrice = parseFloat(discount.original_price) || 0;
    const minQty = discount.min_quantity;
    const tiers = discount.tiers || [];
    const comboRequired = discount.product_combo_required;

    // A discount's value means a different thing per type: a percentage, an
    // amount off, or the new price. Resolve each placeholder from the type
    // instead of printing the raw value.
    const savingFor = (type, value) => {
      if (type === 'PERCENTAGE') return originalPrice * value / 100;
      if (type === 'FIXED_PRICE') return Math.max(originalPrice - value, 0);
      return value;
    };
    const priceFor = (type, value) => {
      if (type === 'FIXED_PRICE') return value;
      return Math.max(originalPrice - savingFor(type, value), 0);
    };
    const percentFor = (type, value) => {
      if (type === 'PERCENTAGE') return `${parseFloat(value.toFixed(2))}%`;
      if (!originalPrice) return '';
      return `${Math.round(savingFor(type, value) / originalPrice * 100)}%`;
    };

    const saving = savingFor(discount.discount_type, discountValue);

    // Get tier value if exists
    let tierValue = savingFor(discount.discount_type, discountValue);
    let tierPercent = percentFor(discount.discount_type, discountValue);
    if (tiers.length > 0) {
      const firstTier = tiers[0];
      const firstTierValue = parseFloat(firstTier.discount_value) || 0;
      tierValue = savingFor(firstTier.discount_type, firstTierValue);
      tierPercent = percentFor(firstTier.discount_type, firstTierValue);
    }

    // Replace placeholders
    return text
      .replace(/\{value\}/gi, `£${saving.toFixed(2)}`)
      .replace(/\{percent\}/gi, percentFor(discount.discount_type, discountValue))
      .replace(/\{amount\}/gi, `£${saving.toFixed(2)}`)
      .replace(/\{price\}/gi, `£${priceFor(discount.discount_type, discountValue).toFixed(2)}`)
      .replace(/\{min_qty\}/gi, minQty || '1')
      .replace(/\{min\}/gi, minQty || '1')
      .replace(/\{combo\}/gi, comboRequired || '0')
      .replace(/\{tier_value\}/gi, `£${tierValue.toFixed(2)}`)
      .replace(/\{tier_percent\}/gi, tierPercent);
  },

  formatDiscountBadge: (discount) => {
    if (!discount) return null;

    const discountValue = parseFloat(discount.discount_value);
    const minQty = discount.min_quantity;
    const tiers = discount.tiers || [];
    const comboRequired = discount.product_combo_required;

    let discountText = '';
    if (tiers.length > 0) {
      const firstTier = tiers[0];
      const tierValue = parseFloat(firstTier.discount_value);
      if (firstTier.discount_type === 'PERCENTAGE') {
        discountText = `-${tierValue}%`;
      } else if (firstTier.discount_type === 'FIXED_AMOUNT') {
        discountText = `-£${tierValue.toFixed(2)}`;
      }
    } else if (discount.discount_type === 'PERCENTAGE') {
      discountText = `-${discountValue}%`;
    } else if (discount.discount_type === 'FIXED_AMOUNT') {
      discountText = `-£${discountValue.toFixed(2)}`;
    } else if (discount.discount_type === 'FIXED_PRICE') {
      discountText = `£${discountValue.toFixed(2)}`;
    }

    if (!discountText) return null;

    // For discounts with PRODUCT_COMBO rule, show "get -X% if" format
    if (comboRequired && comboRequired > 0) {
      return `get ${discountText} if`;
    }

    // For regular discounts with min quantity
    if (minQty) {
      return `${discountText} from ${minQty}+`;
    }

    // For tiers, show first tier condition
    if (tiers.length > 0) {
      return `${discountText} from ${tiers[0].min_quantity}+`;
    }

    return discountText;
  },

  formatDiscountConditionExplanation: (discount) => {
    if (!discount) return null;

    const comboRequired = discount.product_combo_required;
    const minQty = discount.min_quantity;
    const discountValue = parseFloat(discount.discount_value);
    const tiers = discount.tiers || [];

    if (!comboRequired || comboRequired <= 0) return null;

    let discountText = '';
    if (tiers.length > 0) {
      const firstTier = tiers[0];
      const tierValue = parseFloat(firstTier.discount_value);
      if (firstTier.discount_type === 'PERCENTAGE') {
        discountText = `-${tierValue}%`;
      } else if (firstTier.discount_type === 'FIXED_AMOUNT') {
        discountText = `-£${tierValue.toFixed(2)}`;
      }
    } else if (discount.discount_type === 'PERCENTAGE') {
      discountText = `-${discountValue}%`;
    } else if (discount.discount_type === 'FIXED_AMOUNT') {
      discountText = `-£${discountValue.toFixed(2)}`;
    }

    let explanation = `To get ${discountText} add min ${minQty || 1} units for ${comboRequired} products to cart`;

    return explanation;
  },

  hasComboDiscounts: (discountsMap) => {
    if (!discountsMap) return false;
    for (const productId of Object.keys(discountsMap)) {
      const productDiscounts = discountsMap[productId]?.discounts || [];
      for (const discount of productDiscounts) {
        if (discount.product_combo_required && discount.product_combo_required > 0) {
          return true;
        }
      }
    }
    return false;
  },

  getComboDiscountExplanations: (discountsMap) => {
    if (!discountsMap) return [];
    const explanations = new Map();

    for (const productId of Object.keys(discountsMap)) {
      const productDiscounts = discountsMap[productId]?.discounts || [];
      for (const discount of productDiscounts) {
        if (discount.product_combo_required && discount.product_combo_required > 0) {
          const explanation = discountService.formatDiscountConditionExplanation(discount);
          if (explanation && !explanations.has(discount.id)) {
            explanations.set(discount.id, explanation);
          }
        }
      }
    }

    return Array.from(explanations.values());
  },

  // ============ DISCOUNT RULES ============

  getDiscountRules: async (discountId) => {
    const response = await apiClient.get('/prices/discount-rules/', {
      params: { discount_id: discountId }
    });
    return response.data;
  },

  createDiscountRule: async (data) => {
    const response = await apiClient.post('/prices/discount-rules/', data);
    return response.data;
  },

  updateDiscountRule: async (id, data) => {
    const response = await apiClient.patch(`/prices/discount-rules/${id}/`, data);
    return response.data;
  },

  deleteDiscountRule: async (id) => {
    await apiClient.delete(`/prices/discount-rules/${id}/`);
  },

  // ============ QUANTITY TIERS ============

  getQuantityTiers: async (discountId) => {
    const response = await apiClient.get('/prices/quantity-tiers/', {
      params: { discount_id: discountId }
    });
    return response.data;
  },

  createQuantityTier: async (data) => {
    const response = await apiClient.post('/prices/quantity-tiers/', data);
    return response.data;
  },

  updateQuantityTier: async (id, data) => {
    const response = await apiClient.patch(`/prices/quantity-tiers/${id}/`, data);
    return response.data;
  },

  deleteQuantityTier: async (id) => {
    await apiClient.delete(`/prices/quantity-tiers/${id}/`);
  },

  // ============ APPLIED DISCOUNTS (AUDIT) ============

  getAppliedDiscounts: async (params = {}) => {
    const response = await apiClient.get('/prices/applied-discounts/', { params });
    return response.data;
  },

  // ============ HELPER FUNCTIONS ============

  formatDiscountValue: (discount) => {
    if (!discount) return '';

    switch (discount.discount_type) {
      case 'PERCENTAGE':
        return `${discount.discount_value}%`;
      case 'FIXED_PRICE':
        if (discount.is_multi_price) {
          return 'Multi-price';
        }
        return `£${parseFloat(discount.discount_value).toFixed(2)} (fixed price)`;
      case 'FIXED_AMOUNT':
        return `£${parseFloat(discount.discount_value).toFixed(2)} off`;
      default:
        return discount.discount_value;
    }
  },

  isDiscountValid: (discount) => {
    if (!discount) return false;
    if (discount.status !== 'active') return false;

    const now = new Date();
    if (discount.start_date && new Date(discount.start_date) > now) return false;
    if (discount.end_date && new Date(discount.end_date) < now) return false;
    if (discount.max_uses_total && discount.current_uses >= discount.max_uses_total) return false;

    return true;
  },

  getDiscountTypeOptions: () => [
    { value: 'PERCENTAGE', label: 'Percentage Off' },
    { value: 'FIXED_PRICE', label: 'Fixed Price' },
    { value: 'FIXED_AMOUNT', label: 'Fixed Amount Off' },
    { value: 'PRICE_TYPE_SOURCED', label: 'Price Type Sourced' },
  ],

  getModifierTypeOptions: () => [
    { value: 'NONE', label: 'None' },
    { value: 'PERCENT', label: '% Markup/Markdown' },
    { value: 'AMOUNT', label: '£ Markup/Markdown' },
  ],

  getStatusOptions: () => [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'expired', label: 'Expired' },
  ],

  getStackingOptions: () => [
    { value: 'exclusive', label: 'Exclusive - Only this discount applies' },
    { value: 'stackable', label: 'Stackable - Can combine with others' },
    { value: 'best_price', label: 'Best Price - Compare and apply best' },
  ],

  getEvaluationModeOptions: () => [
    { value: 'per_item', label: 'Per Item - Each item evaluated individually' },
    { value: 'all_or_nothing', label: 'All or Nothing - All items must qualify' },
  ],

  getRuleTypeOptions: () => [
    { value: 'MIN_QUANTITY', label: 'Minimum Quantity (per item)' },
    { value: 'MIN_AMOUNT', label: 'Minimum Cart Amount' },
    { value: 'PRODUCT_COMBO', label: 'Product Combination (unique items)' },
    { value: 'FIRST_ORDER', label: 'First Order Only' },
    { value: 'NTH_ORDER', label: 'Nth Order' },
  ],

  getOperatorOptions: (ruleType = null) => {
    const allOperators = [
      { value: 'gte', label: 'Greater than or equal (>=)' },
      { value: 'gt', label: 'Greater than (>)' },
      { value: 'lte', label: 'Less than or equal (<=)' },
      { value: 'lt', label: 'Less than (<)' },
      { value: 'eq', label: 'Equal to (=)' },
      { value: 'between', label: 'Between' },
    ];

    if (ruleType === 'PRODUCT_COMBO') {
      return [{ value: 'gte', label: 'At least (>=)' }];
    }

    if (ruleType === 'NTH_ORDER') {
      return [
        { value: 'gte', label: 'From order # (>=)' },
        { value: 'eq', label: 'Exactly order # (=)' },
        { value: 'between', label: 'Between orders' },
      ];
    }

    return allOperators;
  },

  getPriorityOptions: () => [
    { value: 1, label: '1 - Lowest' },
    { value: 2, label: '2 - Low' },
    { value: 3, label: '3 - Below Normal' },
    { value: 4, label: '4 - Normal-' },
    { value: 5, label: '5 - Normal' },
    { value: 6, label: '6 - Normal+' },
    { value: 7, label: '7 - Above Normal' },
    { value: 8, label: '8 - High' },
    { value: 9, label: '9 - Very High' },
    { value: 10, label: '10 - Highest' },
  ],
};

export default discountService;
